/** Day arithmetic on `YYYY-MM-DD` strings (Amsterdam calendar days, no time zones involved). */
const DAY = 86_400_000

const toUtc = (iso: string): number => {
  const [year, month, day] = iso.split('-').map(Number)
  return Date.UTC(year, month - 1, day)
}

const fromUtc = (ms: number): string => new Date(ms).toISOString().slice(0, 10)

export function addDays(iso: string, days: number): string {
  return fromUtc(toUtc(iso) + days * DAY)
}

/** The Monday of the week of `iso`. */
export function mondayOf(iso: string): string {
  const weekday = new Date(toUtc(iso)).getUTCDay() // 0 = Sunday
  return addDays(iso, -((weekday + 6) % 7))
}

const FORMAT = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('nl-NL', { timeZone: 'UTC', ...options })
const SHORT = FORMAT({ weekday: 'short', day: 'numeric', month: 'short' })
const DAY_MONTH = FORMAT({ day: 'numeric', month: 'short' })
const LONG = FORMAT({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

/** "vr 16 okt". */
export function shortDay(iso: string): string {
  return SHORT.format(new Date(toUtc(iso))).replace(/\./g, '')
}

/** "16 okt". */
export function dayMonth(iso: string): string {
  return DAY_MONTH.format(new Date(toUtc(iso))).replace(/\./g, '')
}

/** "donderdag 15 oktober 2026". */
export function longDay(iso: string): string {
  return LONG.format(new Date(toUtc(iso)))
}

export interface WeekGroup<T> {
  key: string
  label: string
  items: T[]
}

/** Groups dated items into "Deze week", "Volgende week" and "26 okt – 1 nov" (items in date order). */
export function groupByWeek<T extends { date: string }>(items: readonly T[], today: string): WeekGroup<T>[] {
  const thisMonday = mondayOf(today)
  const groups: WeekGroup<T>[] = []
  for (const item of items) {
    const monday = mondayOf(item.date)
    let group = groups.find((candidate) => candidate.key === monday)
    if (!group) {
      const weeks = Math.round((toUtc(monday) - toUtc(thisMonday)) / (7 * DAY))
      const label =
        weeks <= 0 ? 'Deze week' : weeks === 1 ? 'Volgende week' : `${dayMonth(monday)} – ${dayMonth(addDays(monday, 6))}`
      group = { key: monday, label, items: [] }
      groups.push(group)
    }
    group.items.push(item)
  }
  return groups
}

/** The Sunday that ends the week of `iso`. */
export function sundayOf(iso: string): string {
  return addDays(mondayOf(iso), 6)
}
